import axios from "./axios";

const EFFECTIVE_CACHE_KEY = "businessGuarantees.effective.v1";
const pendingEffectiveRequests = new Map();

function getStorage() {
  try {
    if (typeof localStorage === "undefined") {
      return null;
    }

    return localStorage;
  } catch (error) {
    return null;
  }
}

function safeParse(json, fallback) {
  if (!json) {
    return fallback;
  }

  try {
    return JSON.parse(json);
  } catch (error) {
    return fallback;
  }
}

function normalizeScopeValue(value) {
  return value === null || value === undefined || value === "" ? "business" : String(value);
}

function getEffectiveCacheKey({ ID_Business, ID_Branch = null, GuaranteeType }) {
  return [
    String(ID_Business || ""),
    normalizeScopeValue(ID_Branch),
    String(GuaranteeType || ""),
  ].join(":");
}

function readCacheStore() {
  const storage = getStorage();
  if (!storage) {
    return {};
  }

  return safeParse(storage.getItem(EFFECTIVE_CACHE_KEY), {}) || {};
}

function writeCacheStore(store) {
  const storage = getStorage();
  if (!storage) {
    return;
  }

  storage.setItem(EFFECTIVE_CACHE_KEY, JSON.stringify(store));
}

function getEffectiveCacheEntry(scope) {
  const store = readCacheStore();
  return store[getEffectiveCacheKey(scope)] || null;
}

function setEffectiveCacheEntry(scope, entry) {
  const key = getEffectiveCacheKey(scope);
  const store = readCacheStore();
  store[key] = {
    ...entry,
    updatedAt: Date.now(),
  };
  writeCacheStore(store);
}

function clearEffectiveCacheEntry(scope) {
  const key = getEffectiveCacheKey(scope);
  const store = readCacheStore();
  if (!store[key]) {
    return;
  }

  delete store[key];
  writeCacheStore(store);
}

function isEffectivePayloadLoaded(payload) {
  const contentHtml = String(payload?.data?.ContentHtml || "").trim();
  return Boolean(payload?.data) && contentHtml.length > 0;
}

function getEmptyEffectivePayload() {
  return { data: null };
}

export default {
  list(ID_Business) {
    const options = {
      method: "GET",
      url: `/business-guarantees?ID_Business=${ID_Business}`,
      headers: { "content-type": "application/json" },
    };

    return new Promise((resolve, reject) => {
      axios(options)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error.message);
        });
    });
  },

  getEffective({ ID_Business, ID_Branch = null, GuaranteeType }) {
    const scope = {
      ID_Business,
      ID_Branch,
      GuaranteeType,
    };
    const cacheEntry = getEffectiveCacheEntry(scope);

    if (cacheEntry) {
      if (cacheEntry.status === "loaded") {
        return Promise.resolve(cacheEntry.payload || { data: null });
      }

      if (cacheEntry.status === "empty") {
        return Promise.resolve(cacheEntry.payload || getEmptyEffectivePayload());
      }
    }

    const cacheKey = getEffectiveCacheKey(scope);
    if (pendingEffectiveRequests.has(cacheKey)) {
      return pendingEffectiveRequests.get(cacheKey);
    }

    const params = new URLSearchParams();
    params.append("ID_Business", ID_Business);
    params.append("GuaranteeType", GuaranteeType);

    if (ID_Branch !== null && ID_Branch !== undefined && ID_Branch !== "") {
      params.append("ID_Branch", ID_Branch);
    }

    const options = {
      method: "GET",
      url: `/business-guarantees/effective?${params.toString()}`,
      headers: { "content-type": "application/json" },
    };

    const request = new Promise((resolve, reject) => {
      axios(options)
        .then((response) => {
          const payload = response.data;

          if (isEffectivePayloadLoaded(payload)) {
            setEffectiveCacheEntry(scope, {
              status: "loaded",
              payload,
            });
          } else {
            setEffectiveCacheEntry(scope, {
              status: "empty",
              payload: getEmptyEffectivePayload(),
            });
          }

          resolve(payload);
        })
        .catch((error) => {
          reject(error.message);
        })
        .finally(() => {
          pendingEffectiveRequests.delete(cacheKey);
        });
    });

    pendingEffectiveRequests.set(cacheKey, request);
    return request;
  },

  peekEffectiveCache({ ID_Business, ID_Branch = null, GuaranteeType }) {
    return getEffectiveCacheEntry({
      ID_Business,
      ID_Branch,
      GuaranteeType,
    });
  },

  save(data) {
    const options = {
      method: "PUT",
      url: "/business-guarantees",
      headers: { "content-type": "application/json" },
      data,
    };

    return new Promise((resolve, reject) => {
      axios(options)
        .then((response) => {
          const payload = response.data;
          const scope = {
            ID_Business: payload?.data?.ID_Business ?? data?.ID_Business,
            ID_Branch: payload?.data?.ID_Branch ?? data?.ID_Branch ?? null,
            GuaranteeType: payload?.data?.GuaranteeType ?? data?.GuaranteeType,
          };

          if (scope.ID_Business && scope.GuaranteeType) {
            if (isEffectivePayloadLoaded(payload)) {
              setEffectiveCacheEntry(scope, {
                status: "loaded",
                payload,
              });
            } else {
              setEffectiveCacheEntry(scope, {
                status: "empty",
                payload: getEmptyEffectivePayload(),
              });
            }
          }

          resolve(response.data);
        })
        .catch((error) => {
          reject(error.message);
        });
    });
  },

  setActive({ ID_Business, id, IsActive, ID_Branch = null, GuaranteeType = null }) {
    const options = {
      method: "PATCH",
      url: `/business-guarantees/${id}/active`,
      headers: { "content-type": "application/json" },
      data: { ID_Business, IsActive },
    };

    return new Promise((resolve, reject) => {
      axios(options)
        .then((response) => {
          const scope = {
            ID_Business,
            ID_Branch,
            GuaranteeType,
          };

          if (scope.ID_Business && scope.GuaranteeType) {
            if (Number(IsActive) !== 1) {
              clearEffectiveCacheEntry(scope);
            }
          }

          resolve(response.data);
        })
        .catch((error) => {
          reject(error.message);
        });
    });
  },

  updateEffectiveCacheFromRecord(record) {
    if (!record || !record.ID_Business || !record.GuaranteeType) {
      return;
    }

    const scope = {
      ID_Business: record.ID_Business,
      ID_Branch: record.ID_Branch ?? null,
      GuaranteeType: record.GuaranteeType,
    };

    if (Number(record.IsActive) === 1 && String(record.ContentHtml || "").trim()) {
      setEffectiveCacheEntry(scope, {
        status: "loaded",
        payload: {
          data: {
            ContentHtml: record.ContentHtml,
          },
        },
      });
      return;
    }

    setEffectiveCacheEntry(scope, {
      status: "empty",
      payload: getEmptyEffectivePayload(),
    });
  },

  clearEffectiveCacheFromRecord(record) {
    if (!record || !record.ID_Business || !record.GuaranteeType) {
      return;
    }

    clearEffectiveCacheEntry({
      ID_Business: record.ID_Business,
      ID_Branch: record.ID_Branch ?? null,
      GuaranteeType: record.GuaranteeType,
    });
  },
};
